<template>
    <div class="all-offers" ref="offers">
        <DebtSolutionOfferBlock
            :expired="expired"
            :offerIndex="index"
            :debtSolutionOffers="debtSolutionOffers"
            :debtSolutionOffersHeading="$t('results.debtSolutionOffersHeading')"
            :isAgentPage="isAgentPage"
            :offer="offer"
        />

        <FlaggedOfferBlocks
            :expired="expired"
            :flagged-offers="flaggedOffers"
            :flagged-offers-heading="flaggedOffersHeading"
            :false-flagged-offers-headings="falseFlaggedOffersHeadings"
            :has-false-flagged-offers="falseFlaggedOffers"
            :is-agent-page="isAgentPage"
            :loan-amount="loanAmount"
            :loanTerm="loanTerm"
        />

        <div v-if="standardOffers.length" class="standard-offers">
            <div v-for="(offer, index) in this.standardOffers" :key="offer.id" class="offer-block">
                <OfferBlock
                    :offer="offer"
                    :offerIndex="index"
                    :offerCount="standardOffers.length"
                    :expired="expired"
                    :isAgentPage="isAgentPage"
                    :loanAmount="loanAmount"
                    :loanTerm="loanTerm"
                    :loanPurpose="loanPurpose"
                >
                    <OfferContent
                        :offer="offer"
                        :loanAmount="loanAmount"
                        :loanTerm="loanTerm"
                        :fastFundingFlagStatus="fastFundingFlagStatus"
                    />
                </OfferBlock>
            </div>
        </div>

        <div v-if="itaOffers.length" class="eligible-offers">
            <div>
                <h2 class="offer-heading">
                    {{ itaOffersHeading }}
                </h2>
            </div>

            <div v-for="(offer, index) in this.itaOffers" :key="index" class="offer-block">
                <OfferBlock
                    :offer="offer"
                    :isAgentPage="isAgentPage"
                    :offerCount="itaOffers.length"
                    :expired="expired"
                    :offerIndex="index"
                    :loanAmount="loanAmount"
                    :loanTerm="loanTerm"
                    :loanPurpose="loanPurpose"
                >
                    <OfferContent
                        :loanAmount="loanAmount"
                        :offer="offer"
                        :loanTerm="loanTerm"
                        :fastFundingFlagStatus="fastFundingFlagStatus"
                    />
                </OfferBlock>
            </div>
        </div>

        <div
            v-if="
                (!isLendindtree && alternativeOffersNonFlagged.length && !hasFlaggedOffer) ||
                (isLendindtree && alternativeOffers)
            "
            class="alternative-offers"
        >
            <h2 class="offer-heading">
                {{ alternativeOfferHeading }}
            </h2>

            <div v-for="(offer, index) in this.alternativeOffersNonFlagged" :key="offer.id" class="offer-block">
                <OfferBlock
                    :expired="expired"
                    :loanAmount="loanAmount"
                    :offer="offer"
                    :offerIndex="index"
                    :isAlternativeOffer="offer.type !== 'credit card only' && offer.type !== 'debt_solution'"
                    :loanTerm="loanTerm"
                    :loanPurpose="loanPurpose"
                >
                    <OfferContent
                        v-if="offer.type === 'credit card only' || offer.type === 'debt_solution'"
                        :loanAmount="loanAmount"
                        :offer="offer"
                        :loanTerm="loanTerm"
                        :fastFundingFlagStatus="fastFundingFlagStatus"
                        :loanPurpose="loanPurpose"
                    />

                    <AlternativeOfferContent v-else :loanAmount="loanAmount" :offer="offer" :loanTerm="loanTerm" />
                </OfferBlock>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { mapGetters, useStore } from 'vuex'

// lodash components
import minBy from 'lodash/minBy'
import filter from 'lodash/filter'
import _get from 'lodash/get'
import some from 'lodash/some'

export default {
    setup() {
        const store = useStore()

        return {
            store,
        }
    },
    data() {
        return {
            trackedOffers: [],
        }
    },
    mounted() {
        const offersElement = this.$refs.offers
        const observerConfig = this.getObserverConfig()

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.intersectionRatio >= 0.7 && !this.hasTriggered) {
                    this.generateEventDataAndTrackEvent()
                    this.hasTriggered = true
                    observer.unobserve(entry.target)
                }
            })
        }, observerConfig)

        if (offersElement) {
            observer.observe(offersElement)
        }

        this.observer = observer
    },
    beforeDestroy() {
        const offersElement = this.$refs.offers
        if (offersElement && this.observer) {
            this.observer.unobserve(offersElement)
        }
    },
    props: [
        'offers',
        'declinedLenders',
        'expired',
        'isAgentPage',
        'applicationData',
        'ignoreOrdering',
        'fastFundingFlagStatus',
        'ignoreCreditCardAlternativeOValidation',
        'alternativeOffers',
        'offerIndex',
    ],
    computed: {
        ...mapGetters(['sortOptions', 'offerResultDataFilters']),
        territory: function () {
            return process.env.TERRITORY
        },
        lowestRate: function () {
            const nonCreditCardOnly = filter(this.standardOffers, offer => {
                return offer.type !== 'credit card only'
            })

            const filteredOffers = minBy(nonCreditCardOnly, 'apr.min')
            return filteredOffers && filteredOffers.apr && filteredOffers.apr.min ? filteredOffers : false
        },
        loanAmount: function () {
            return this.applicationData.loan_amount
        },
        loanTerm: function () {
            return this.applicationData.loan_term
        },
        loanPurpose: function () {
            return this.applicationData.loan_purpose
        },
        standardOffers: function () {
            if (this.ignoreOrdering && !this.hasFlaggedOffer) {
                return filter(this.offers, offer => {
                    return (
                        !this.isAlternativeOffer(offer) &&
                        !this.isFlaggedOffer(offer) &&
                        !this.isUkDebtSolutionOffer(offer)
                    )
                })
            }

            return filter(this.offers, offer => {
                return (
                    !this.isAlternativeOffer(offer) &&
                    !this.isRangeValue(offer, 'loan_amount') &&
                    !this.hasFlaggedOffer &&
                    !this.isUkDebtSolutionOffer(offer)
                )
            })
        },
        itaOffers: function () {
            if (!this.ignoreOrdering) {
                return filter(this.offers, offer => {
                    return this.isItaOffers(offer)
                })
            }
            return []
        },
        flaggedOffers: function () {
            return filter(this.offers, offer => {
                const isFlagged = this.isFlaggedOffer(offer)
                const isDebtSolution = offer.type === 'debt_solution'

                if (this.territory === 'gbr') {
                    return isFlagged && !isDebtSolution
                } else {
                    return isFlagged
                }
            })
        },
        falseFlaggedOffers: function () {
            if (this.ignoreOrdering && this.hasFlaggedOffer) {
                return filter(this.offers, offer => {
                    return !this.isAlternativeOffer(offer) && !this.isFlaggedOffer(offer)
                })
            }

            return filter(this.offers, offer => {
                return !this.isFlaggedOffer(offer) && !this.isItaOffers(offer) && this.hasFlaggedOffer
            })
        },
        alternativeOffersNonFlagged: function () {
            if (
                this.includeTerritories('usa') &&
                this.creditCardCampaign &&
                !this.ignoreCreditCardAlternativeOValidation
            ) {
                let preCount = some(this.offers, { pre_approved: true } && { pre_qualified: true })

                if (preCount) {
                    return []
                }
            }

            return filter(this.offers, offer => {
                return this.isAlternativeOffer(offer) && !this.isFlaggedOffer(offer)
            })
        },
        itaOffersHeading: function () {
            return this.$t('results.alternativeServices')
        },
        flaggedOffersHeading: function () {
            if (!this.hasFlaggedOffer) {
                return false
            }

            return this.hasFlaggedOffer
                ? this.$t('results.flaggedOffersHeadings')
                : this.$t('results.flaggedOfferHeading')
        },
        falseFlaggedOffersHeadings: function () {
            if (this.falseFlaggedOffers.length && this.hasFlaggedOffer) {
                return this.falseFlaggedOffers.length > 1
                    ? this.$t('results.falseFlaggedOffersHeadings')
                    : this.$t('results.falseFlaggedOfferHeading')
            }

            return false
        },
        alternativeOfferHeading: function () {
            if (!this.standardOffers.length && this.alternativeOffersNonFlagged.length && !this.isLendindtree) {
                return this.$t('results.noLoanAlternativeServices')
            } else if (this.isLendindtree) {
                return this.$t('results.alternativeLendindtree')
            } else {
                return this.$t('results.alternativeServices')
            }
        },
        creditCardCampaign: function () {
            return window.CAMPAIGN_TYPE === 'Credit Card'
        },
        hasFlaggedOffer: function () {
            return some(this.offers, { flagged_offer: true })
        },
        debtSolutionOffers: function () {
            if (this.territory === 'gbr') {
                return filter(this.offers, offer => {
                    return offer.type === 'debt_solution'
                })
            }
        },
        isLendindtree: function () {
            return process.env.IS_LENDINDTREE
        },
    },
    methods: {
        getObserverConfig() {
            return {
                root: null,
                rootMargin: '1000% 0% 0% 0%',
                threshold: Array.from({ length: 20 }, (_, i) => (i + 1) * 0.05), // Creates thresholds at every 5% from 5% to 100%
            }
        },
        getSortEventData() {
            const currentSort = this.sortOptions
            let sortIndex = 0
            let sortEventData = {}

            for (let i = 0; i < currentSort.length; i++) {
                const sortItem = currentSort[i]
                if (sortItem.active) {
                    sortEventData = {
                        sortCategory: sortItem.name,
                        sortIndex: sortIndex,
                    }
                    break
                }
                sortIndex++
            }

            return sortEventData
        },
        generateEventDataAndTrackEvent() {
            const sortEventData = this.getSortEventData()

            const eventData = {
                campaign_code: window.CAMPAIGN_CODE,
                campaign_integration: window.CAMPAIGN_INTEGRATION,
                application_id: this.$store.state.result.allAppData.application_data?.reference,
                territory: process.env.TERRITORY,
                sortCategory: sortEventData?.sortCategory,
                sortIndex: sortEventData?.sortIndex,
                store: this.store, //needed because it does not have the same context when call through IntersectionObserver
            }

            this.trackEvent('results_offer_displayed', eventData)
        },
        isRangeValue(offer, field) {
            return _get(offer, `${field}.min`) && _get(offer, `${field}.max`)
        },
        isFlaggedOffer(offer) {
            return offer.flagged_offer === true
        },
        isUkDebtSolutionOffer(offer) {
            if (this.territory === 'gbr' && offer.type === 'debt_solution') {
                return true
            }

            return false
        },
        isAlternativeOffer(offer) {
            if (window.CAMPAIGN_TYPE !== 'Credit Card') {
                return (
                    offer.type === 'alternative' ||
                    (this.territory === 'usa' && (offer.type === 'credit card only' || offer.type === 'debt_solution'))
                )
            }

            return offer.type === 'alternative' || (this.territory === 'usa' && offer.type === 'debt_solution')
        },
        isItaOffers(offer) {
            return (
                !this.isAlternativeOffer(offer) &&
                this.isRangeValue(offer, 'loan_amount') &&
                !this.isFlaggedOffer(offer)
            )
        },
    },
    provide() {
        return {
            lowestOffersRate: computed(() => this.lowestRate),
            sortData: computed(() => this.getSortEventData()),
        }
    },
}
</script>

<style lang="scss">
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.offers-group {
    border-bottom: 1px solid #e5e5e5;
    padding-bottom: 70px;

    .offer-heading {
        font-family: var(--heading-font);
        font-size: rem(30px);
        font-style: normal;
        font-weight: 600;
        margin-top: 3rem;
        margin-bottom: rem(30px);

        @media screen and (max-width: $bp-small) {
            font-size: rem(27px);
            margin: rem(12.6px);
            text-align: justify;
        }
    }
}

.eligible-offers,
.alternative-offers {
    border-bottom: 1px solid #e5e5e5;
    padding-bottom: 70px;

    .offer-heading {
        font-family: var(--heading-font);
        font-size: rem(30px);
        font-style: normal;
        font-weight: 600;
        margin-top: 3rem;
        margin-bottom: rem(30px);

        @media screen and (max-width: $bp-small) {
            font-size: rem(20px);
            margin: 0.7rem;
            text-align: left;
        }
    }

    @media screen and (max-width: $bp-small) {
        padding-bottom: 0px;
    }
}

.application-terms {
    border-bottom: 1px solid #e5e5e5;
    color: #858585;
    font-size: rem(14px);
    margin-top: 30px;
    padding-bottom: 70px;
}

.declined-lenders {
    .count {
        background-color: #e6f0f5;
        border-radius: 5px;
        padding: 2px 8px;
    }

    .declined-description {
        margin-top: 21px;
    }
}

.offers-group {
    border-bottom: 1px solid #e5e5e5;
    padding-bottom: 70px;
}
</style>
